import { type TranslationFunction } from 'tradera/lang/translate';
import { CONSTANTS } from '../constants';
import TraderaCircleSVG from 'tradera/apps/tradera/messaging/images/tradera-circle.react.svg';
import type {
    FirebaseThread,
    FirebaseMessagingRecipient,
    FirebaseProfile
} from 'tradera/apps/tradera/messaging/types';
import { timestampToDate } from 'tradera/apps/tradera/messaging/utils/firebase-timestamp';

const getCustomerServiceRecipient = (
    t: TranslationFunction
): FirebaseMessagingRecipient => ({
    userId: CONSTANTS.FIREBASE.CUSTOMER_SERVICE_ID,
    username: t('messaging_tradera_customer_service'),
    thumbnailUrl: TraderaCircleSVG,
    blocked: false,
    countryCodeIso2: 'SE',
    suspended: false
});

function getThreadId(memberId1: number, memberId2: number, listingId: number) {
    return `u${Math.min(memberId1, memberId2)}_u${Math.max(
        memberId1,
        memberId2
    )}_l${listingId}`;
}

type ThreadInfo = { senderId: string; receiverId: string; listingId: string };

function getThreadInfo(threadId: string, senderId: string): ThreadInfo | null {
    const threadInfo = threadId.split('_').map((part) => part.substring(1));
    if (threadInfo.length === 3) {
        const [userMin, userMax, listingId] = threadInfo;
        return {
            senderId: userMin === senderId ? userMin : userMax,
            receiverId: userMin !== senderId ? userMin : userMax,
            listingId
        };
    } else {
        return null;
    }
}

function getRecipient(
    thread: FirebaseThread,
    senderId: string,
    t: TranslationFunction
): FirebaseMessagingRecipient | null {
    let recipient = null;
    if (!thread) return null;
    if (!thread.members) return getCustomerServiceRecipient(t);

    // Find user that isn't sender or customer service
    const userId = Object.keys(thread.members).find((key) => {
        return (
            key !== senderId && key !== CONSTANTS.FIREBASE.CUSTOMER_SERVICE_ID
        );
    });
    if (userId && userId in thread.members) {
        const {
            username,
            thumbnailUrl,
            suspendedState,
            countryCodeIso2,
            messagePermission
        } = thread.members[userId];
        recipient = {
            userId,
            username,
            thumbnailUrl,
            countryCodeIso2,
            suspended:
                suspendedState !== CONSTANTS.FIREBASE.SUSPENDED_STATE.NONE
                    ? suspendedState
                    : false,
            blocked:
                messagePermission ===
                CONSTANTS.FIREBASE.MESSAGE_PERMITION_TYPE.BLOCKED
        } as FirebaseMessagingRecipient;
        return recipient;
    } else {
        return getCustomerServiceRecipient(t);
    }
}

function getThreadInteractivity(
    thread: FirebaseThread,
    recipient: FirebaseMessagingRecipient,
    userId: string,
    userProfile: FirebaseProfile | null,
    t: TranslationFunction
): ThreadInteractivity {
    const { members, lockedAt, markedAsSpam } = thread || {};

    const hasNoMembers = !members;
    if (hasNoMembers) {
        return {
            addMessageText: t('messaging_warnings_user_not_available'),
            addMessageTextSeverity: 'warning',
            isAddMessageAllowed: false
        };
    }

    if (lockedAt) {
        return {
            addMessageText: t('messaging_warnings_closed_by_cs'),
            addMessageTextSeverity: 'info',
            isAddMessageAllowed: false
        };
    }

    if (markedAsSpam != null) {
        return {
            addMessageText: t('messaging_warnings_marked_as_spam'),
            addMessageTextSeverity: 'warning',
            isAddMessageAllowed: false
        };
    }

    const threadEnded =
        members[userId].messagePermission ===
        CONSTANTS.FIREBASE.MESSAGE_PERMITION_TYPE.ENDED;
    if (threadEnded) {
        return {
            addMessageText: t('messaging_warnings_listing_ended'),
            addMessageTextSeverity: 'info',
            isAddMessageAllowed: false
        };
    }

    const memberBlocked =
        members[userId].messagePermission ===
        CONSTANTS.FIREBASE.MESSAGE_PERMITION_TYPE.BLOCKED;
    if (memberBlocked) {
        return {
            addMessageText: t('messaging_warnings_user_not_available'),
            addMessageTextSeverity: 'warning',
            isAddMessageAllowed: false
        };
    }

    const recipientSuspended =
        recipient.suspended === CONSTANTS.FIREBASE.SUSPENDED_STATE.TOTAL;
    if (recipientSuspended) {
        return {
            addMessageText: t('messaging_warnings_suspended_total'),
            addMessageTextSeverity: 'warning',
            isAddMessageAllowed: false
        };
    }

    const recipientPartiallySuspended =
        recipient.suspended === CONSTANTS.FIREBASE.SUSPENDED_STATE.PARTIAL;
    if (recipientPartiallySuspended) {
        return {
            addMessageText: t('messaging_warnings_suspended_partial'),
            addMessageTextSeverity: 'info',
            isAddMessageAllowed: true
        };
    }

    if (userProfile?.messageThrottledUntil) {
        if (timestampToDate(userProfile.messageThrottledUntil) > new Date()) {
            return {
                addMessageText: t('messaging_warnings_throttled'),
                addMessageTextSeverity: 'info',
                isAddMessageAllowed: false
            };
        }
    }

    if (userProfile?.requireIdentityVerification) {
        return {
            addMessageText: t(
                'messaging_warnings_require_identity_verification'
            ),
            addMessageTextSeverity: 'warning',
            isAddMessageAllowed: false
        };
    }

    if (recipient.blocked) {
        return {
            addMessageText: t('messaging_warnings_user_in_your_blocked_list'),
            addMessageTextSeverity: 'warning',
            isAddMessageAllowed: false
        };
    }

    return { isAddMessageAllowed: true };
}

function sortThreads(threads: FirebaseThread[]) {
    return threads.sort((a, b) => {
        const aValue = a.lastMessageAt || a.createdAt;
        const bValue = b.lastMessageAt || b.createdAt;
        if (aValue < bValue) return 1;
        if (aValue > bValue) return -1;
        return 0;
    });
}

export type ThreadInteractivity = {
    isAddMessageAllowed: boolean;
    addMessageText?: string;
    addMessageTextSeverity?: 'info' | 'warning';
};

export {
    getCustomerServiceRecipient,
    getThreadId,
    getThreadInfo,
    getRecipient,
    getThreadInteractivity,
    sortThreads
};
